import { TextInput, TextInputProps } from './TextInput';
import { useState } from 'react';

type RequiredTextInputProps = Omit<
  TextInputProps,
  'indicator' | 'errorMessage' | 'isRequired' | 'hasError' | 'defaultValue'
> &
  Required<Pick<TextInputProps, 'errorMessage'>> & {
    /**
     * Some forms validate inputs on hitting the submit button, whereas other disable the button untill all fields are valid.
     * For the former one, pass 'shouldShowError' prop to show error without user touching the input.
     */
    shouldShowError?: boolean;
  };

/**
 * Used for required input, mainly for names of different assets.
 * Don't overcomplicate it with other validations - if you need sth more specific, create a separate component.
 *
 * Pass errorMessage that should be displayed when user blurs input without providing value.
 * Shows error only after focusing out.
 *
 * Some forms validate inputs on hitting the submit button, whereas other disable the button untill all fields are valid.
 * For the former one, pass 'shouldShowError' prop to show error without user touching the input.
 *
 * This component is stateful - it keeps the information if it has been touched.
 *
 */
export const RequiredTextInput = ({
  shouldShowError,
  errorMessage,
  onBlur,
  ...rest
}: RequiredTextInputProps) => {
  const [touched, setTouched] = useState(false);
  const valueEmpty =
    typeof rest.value === 'string' ? rest.value.trim() === '' : !rest.value;
  const hasError = valueEmpty && (touched || shouldShowError);

  return (
    <TextInput
      {...rest}
      indicator="required"
      hasError={hasError}
      errorMessage={hasError ? errorMessage : ''}
      onBlur={event => {
        setTouched(true);
        onBlur?.(event);
      }}
    />
  );
};
